import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, X as XIcon, Sparkles, Users, Eye, RefreshCw, Clock, ArrowUp, ArrowDown, ArrowUpDown, Handshake } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import DealsPanel from "@/components/admin/DealsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = [
  "BYD", "Caterpillar", "Clark", "Combilift", "Crown",
  "Doosan", "Heli", "Hangcha", "Hyster", "Hyundai",
  "JAC", "Jungheinrich", "Kalmar", "Komatsu", "Linde",
  "Manitou", "Mitsubishi", "Nissan", "Raymond", "TCM",
  "Toyota", "UniCarriers", "Yale", "Other",
];

const COLOR_PALETTE = [
  "#dc2626", "#ea580c", "#ca8a04", "#16a34a",
  "#0d9488", "#1d4ed8", "#7c3aed", "#db2777",
  "#374151", "#92400e", "#1e40af", "#065f46",
];

// Age indicator: green < 3 weeks, yellow 3 weeks, orange >= 4 weeks
const getAgeInfo = (forklift) => {
  const ref = forklift.availability_confirmed_at || forklift.created_at;
  if (!ref) return { days: 0, weeks: 0, label: "New", rowClass: "bg-green-50 hover:bg-green-100", badgeClass: "bg-green-100 text-green-700" };
  const diffDays = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 21) return { days: diffDays, weeks, label: weeks === 0 ? "< 1w" : `${weeks}w`, rowClass: "bg-green-50 hover:bg-green-100", badgeClass: "bg-green-100 text-green-700" };
  if (diffDays < 28) return { days: diffDays, weeks, label: `${weeks}w`, rowClass: "bg-yellow-50 hover:bg-yellow-100", badgeClass: "bg-yellow-100 text-yellow-700" };
  return { days: diffDays, weeks, label: `${weeks}w`, rowClass: "bg-orange-50 hover:bg-orange-100", badgeClass: "bg-orange-100 text-orange-700" };
};

const emptyForm = {
  brand: "", model: "", condition: "new", price: "",
  capacity_lbs: "", engine_type: "propane", mast_stages: "2",
  tire_type: "pneumatic", drive: "2wd", year: "",
  hours_used: "", description_en: "", description_fr: "",
  image_url: "", images: [], status: "available",
  supplier: "", admin_notes: "",
};

export default function AdminInventory() {
  const navigate = useNavigate();
  const [forklifts, setForklifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [uploading, setUploading] = useState(false);
  const [smartText, setSmartText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [matchAlert, setMatchAlert] = useState(null);
  const [dialogTab, setDialogTab] = useState("smart");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dealsForklift, setDealsForklift] = useState(null); // open the Deals modal for this forklift

  // Supplier state
  const [suppliers, setSuppliers] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierColor, setNewSupplierColor] = useState("#1d4ed8");
  const [addingSupplier, setAddingSupplier] = useState(false);

  // Sorting state
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedForklifts = React.useMemo(() => {
    if (!sortKey) return forklifts;
    const arr = [...forklifts];
    arr.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      // Brand/model compound sort
      if (sortKey === "brand") {
        av = `${a.brand || ""} ${a.model || ""}`.toLowerCase();
        bv = `${b.brand || ""} ${b.model || ""}`.toLowerCase();
      }
      // Numeric fields
      if (sortKey === "price" || sortKey === "capacity_lbs") {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      }
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [forklifts, sortKey, sortDir]);

  const fetchForklifts = async () => {
    try {
      const { data } = await axios.get(`${API}/forklifts`, { params: { status: "all" }, withCredentials: true });
      setForklifts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/suppliers`, { withCredentials: true });
      setSuppliers(data);
    } catch (err) {
      console.error("Failed to load suppliers:", err);
    }
  };

  useEffect(() => { fetchForklifts(); fetchSuppliers(); }, []);

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleAddSupplier = async () => {
    if (!newSupplierName.trim()) { toast.error("Enter a supplier name."); return; }
    setAddingSupplier(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/suppliers`,
        { name: newSupplierName.trim(), color: newSupplierColor },
        { withCredentials: true }
      );
      setSuppliers((prev) => [...prev, data]);
      handleChange("supplier", data.id);
      setNewSupplierName("");
      setShowAddSupplier(false);
      toast.success(`Supplier "${data.name}" added!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add supplier");
    } finally {
      setAddingSupplier(false);
    }
  };

  const getSupplierById = (id) => suppliers.find((s) => s.id === id);

  const handleConfirmAvailability = async (forkliftId) => {
    try {
      await axios.post(`${API}/forklifts/${forkliftId}/confirm-availability`, {}, { withCredentials: true });
      toast.success("Timer reset — machine marked as confirmed today");
      fetchForklifts();
    } catch (err) {
      toast.error("Failed to reset timer");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setSmartText("");
    setDialogTab("smart");
    setShowAddSupplier(false);
    setNewSupplierName("");
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setDialogTab("form");
    setShowAddSupplier(false);
    setNewSupplierName("");
    setForm({
      brand: f.brand || "",
      model: f.model || "",
      condition: f.condition || "new",
      price: f.price || "",
      capacity_lbs: f.capacity_lbs || "",
      engine_type: f.engine_type || "propane",
      mast_stages: String(f.mast_stages || "2"),
      tire_type: f.tire_type || "pneumatic",
      drive: f.drive || "2wd",
      year: f.year || "",
      hours_used: f.hours_used || "",
      description_en: f.description_en || "",
      description_fr: f.description_fr || "",
      image_url: f.image_url || "",
      images: f.images || [],
      status: f.status || "available",
      supplier: f.supplier || "",
      admin_notes: f.admin_notes || "",
    });
    setDialogOpen(true);
  };

  const handleSmartParse = async () => {
    if (!smartText.trim()) { toast.error("Please enter machine information."); return; }
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/parse-forklift`, { text: smartText }, { withCredentials: true });
      const p = data.parsed;
      setForm({
        brand: p.brand || "",
        model: p.model || "",
        condition: p.condition || "used",
        price: p.price != null ? String(p.price) : "",
        capacity_lbs: p.capacity_lbs != null ? String(p.capacity_lbs) : "",
        engine_type: p.engine_type || "propane",
        mast_stages: p.mast_stages != null ? String(p.mast_stages) : "3",
        tire_type: p.tire_type || "pneumatic",
        drive: p.drive || "2wd",
        year: p.year != null ? String(p.year) : "",
        hours_used: p.hours_used != null ? String(p.hours_used) : "",
        description_en: p.description_en || "",
        description_fr: p.description_fr || "",
        image_url: "",
        images: [],
        status: "available",
        supplier: "",
        admin_notes: "",
      });
      setDialogTab("form");
      toast.success("Fields auto-filled! Review and save.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse text. Try again or enter manually.");
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!form.brand) { toast.error("At least a brand name is required."); return; }
    const body = {
      ...form,
      price: form.price ? parseFloat(form.price) : 0,
      capacity_lbs: form.capacity_lbs ? parseInt(form.capacity_lbs) : 0,
      mast_stages: form.mast_stages ? parseInt(form.mast_stages) : 3,
      year: form.year ? parseInt(form.year) : null,
      hours_used: form.hours_used ? parseInt(form.hours_used) : null,
      supplier: form.supplier || null,
      admin_notes: form.admin_notes || null,
      images: form.images || [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/forklifts/${editing.id}`, body, { withCredentials: true });
        toast.success("Forklift updated");
      } else {
        const { data } = await axios.post(`${API}/forklifts`, body, { withCredentials: true });
        if (data.matching_clients && data.matching_clients.length > 0) {
          setMatchAlert(data.matching_clients);
          toast.success(`Forklift added — ${data.matching_clients.length} matching client(s)!`);
        } else {
          toast.success("Forklift added");
        }
      }
      setDialogOpen(false);
      fetchForklifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving forklift");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/forklifts/${id}`, { withCredentials: true });
      toast.success("Forklift deleted");
      setDeleteTarget(null);
      fetchForklifts();
    } catch (err) {
      toast.error("Error deleting forklift");
    }
  };

  const supplierProps = {
    suppliers, getSupplierById,
    showAddSupplier, setShowAddSupplier,
    newSupplierName, setNewSupplierName,
    newSupplierColor, setNewSupplierColor,
    handleAddSupplier, addingSupplier,
  };

  return (
    <div data-testid="admin-inventory" className="p-8">
      {/* Match Alert */}
      {matchAlert && matchAlert.length > 0 && (
        <div data-testid="match-alert" className="mb-6 bg-green-50 border border-green-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              <h3 className="font-heading text-sm font-bold text-green-800">
                {matchAlert.length} client(s) match this new machine!
              </h3>
            </div>
            <button onClick={() => setMatchAlert(null)} className="text-green-400 hover:text-green-600"><XIcon className="w-4 h-4" /></button>
          </div>
          <div className="space-y-1.5">
            {matchAlert.map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-white border border-green-100 rounded-sm px-3 py-2">
                <div>
                  <span className="text-sm font-medium text-gray-900">{c.name}</span>
                  {c.phone && <span className="text-xs text-gray-500 ml-2">{c.phone}</span>}
                </div>
                <div className="flex gap-2">
                  {c.request_id && (
                    <button onClick={() => navigate(`/admin/requests/${c.request_id}`)} className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded-sm">
                      View Request
                    </button>
                  )}
                  {c.crm_client_id && (
                    <button onClick={() => navigate(`/admin/crm/clients/${c.crm_client_id}`)} className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 rounded-sm">
                      View in CRM
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">{forklifts.length} forklifts total</p>
        </div>
        <button
          data-testid="add-forklift-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-4 py-2 rounded-sm text-sm"
        >
          <Plus className="w-4 h-4" /> Add Forklift
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {[
                { key: "brand", label: "Brand / Model", align: "left" },
                { key: "condition", label: "Condition", align: "left" },
                { key: "engine_type", label: "Engine", align: "left" },
                { key: "capacity_lbs", label: "Capacity", align: "left" },
                { key: "price", label: "Price", align: "left" },
                { key: "status", label: "Status", align: "left" },
              ].map((col) => {
                const active = sortKey === col.key;
                const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <TableHead
                    key={col.key}
                    data-testid={`inventory-sort-${col.key}`}
                    onClick={() => toggleSort(col.key)}
                    className="font-semibold cursor-pointer select-none hover:bg-gray-100 transition-colors"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label}
                      <Icon className={`w-3 h-3 ${active ? "text-yellow-600" : "text-gray-300"}`} />
                    </span>
                  </TableHead>
                );
              })}
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell>
              </TableRow>
            ) : sortedForklifts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No forklifts yet. Add your first one!
                </TableCell>
              </TableRow>
            ) : (
              sortedForklifts.map((f) => {
                const sup = f.supplier ? getSupplierById(f.supplier) : null;
                const age = getAgeInfo(f);
                return (
                  <TableRow key={f.id} data-testid={`inventory-row-${f.id}`} className={age.rowClass}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-10 rounded-sm overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                          {f.image_url ? (
                            <img
                              src={f.image_url.startsWith("/api/") ? `${API.replace('/api', '')}${f.image_url}` : f.image_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-gray-400 font-medium">
                              No img
                            </div>
                          )}
                        </div>
                        <div>
                          <Link to={`/inventory/${f.id}`} target="_blank" className="font-medium text-gray-900 hover:text-yellow-700 transition-colors">
                            {f.brand} {f.model}
                          </Link>
                          <p className="text-xs text-gray-500">{f.year || "—"}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span
                              data-testid={`age-badge-${f.id}`}
                              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${age.badgeClass}`}
                              title={`${age.days} days in inventory`}
                            >
                              <Clock className="w-2.5 h-2.5" /> {age.label}
                            </span>
                            {sup && (
                              <span
                                data-testid={`supplier-badge-${f.id}`}
                                style={{ backgroundColor: sup.color + "18", color: sup.color, borderColor: sup.color + "40" }}
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded border inline-block leading-tight"
                              >
                                {sup.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={f.condition === "new" ? "bg-yellow-100 text-yellow-800 border-none hover:bg-yellow-100" : "bg-gray-100 text-gray-700 border-none hover:bg-gray-100"}>
                        {f.condition}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{f.engine_type}</TableCell>
                    <TableCell className="font-mono text-sm">{f.capacity_lbs?.toLocaleString()} lbs</TableCell>
                    <TableCell className="font-mono text-sm">${f.price?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={f.status === "available" ? "bg-green-100 text-green-800 border-none hover:bg-green-100" : "bg-red-100 text-red-800 border-none hover:bg-red-100"}>
                        {f.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`confirm-availability-${f.id}`}
                          onClick={() => handleConfirmAvailability(f.id)}
                          title="Confirm still available — reset timer to today"
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-sm"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <Link
                          to={`/inventory/${f.id}`}
                          target="_blank"
                          data-testid={`view-forklift-${f.id}`}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          data-testid={`deals-forklift-${f.id}`}
                          onClick={() => setDealsForklift(f)}
                          title="Voir les clients reliés (deals)"
                          className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-sm"
                        >
                          <Handshake className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`edit-forklift-${f.id}`}
                          onClick={() => openEdit(f)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-sm"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-forklift-${f.id}`}
                          onClick={() => setDeleteTarget(f)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Forklift" : "Add New Forklift"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update the forklift details below." : "Paste machine info for auto-fill, or enter details manually."}
            </DialogDescription>
          </DialogHeader>

          {!editing ? (
            <Tabs value={dialogTab} onValueChange={setDialogTab} className="mt-2">
              <TabsList className="w-full">
                <TabsTrigger value="smart" className="flex-1 gap-1.5" data-testid="tab-smart">
                  <Sparkles className="w-3.5 h-3.5" /> Smart Entry
                </TabsTrigger>
                <TabsTrigger value="form" className="flex-1" data-testid="tab-form">
                  Manual Form
                </TabsTrigger>
              </TabsList>
              <TabsContent value="smart">
                <div className="space-y-4 mt-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3">
                    <p className="text-sm text-gray-700 font-medium">Paste or type all the machine details below</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Include any info: brand, model, year, engine type, capacity, price, hours, condition, tires, drive, etc. The AI will fill in all fields automatically.
                    </p>
                  </div>
                  <Textarea
                    data-testid="smart-entry-input"
                    value={smartText}
                    onChange={(e) => setSmartText(e.target.value)}
                    placeholder={"Example:\nToyota 8FGU25, 2020, propane, 5000 lbs capacity, 3-stage mast, pneumatic tires, 2WD, 3200 hours, used, asking $18,500.\n\nOr paste a spec sheet, ad listing, or any text with machine details..."}
                    className="min-h-[180px] bg-white text-sm"
                  />
                  <button
                    data-testid="smart-parse-btn"
                    onClick={handleSmartParse}
                    disabled={parsing}
                    className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-6 py-3 rounded-sm disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    {parsing ? "Analyzing..." : "Auto-Fill Fields"}
                  </button>
                </div>
              </TabsContent>
              <TabsContent value="form">
                <FormFields form={form} handleChange={handleChange} uploading={uploading} setUploading={setUploading} {...supplierProps} />
                <div className="mt-6 flex justify-end gap-3">
                  <button data-testid="dialog-cancel-btn" onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200">Cancel</button>
                  <button data-testid="dialog-save-btn" onClick={handleSave} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm">Add Forklift</button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <FormFields form={form} handleChange={handleChange} uploading={uploading} setUploading={setUploading} {...supplierProps} />
              <div className="mt-6 flex justify-end gap-3">
                <button data-testid="dialog-cancel-btn" onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200">Cancel</button>
                <button data-testid="dialog-save-btn" onClick={handleSave} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm">Update</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deals modal — show clients linked to this forklift */}
      <Dialog open={!!dealsForklift} onOpenChange={(open) => !open && setDealsForklift(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="deals-modal-title">
              {dealsForklift?.brand} {dealsForklift?.model}
              {dealsForklift?.year ? ` (${dealsForklift.year})` : ""}
            </DialogTitle>
            <DialogDescription>
              Clients reliés à cette machine — vendeurs, acheteurs et prospects intéressés.
            </DialogDescription>
          </DialogHeader>
          {dealsForklift && <DealsPanel mode="forklift" forkliftId={dealsForklift.id} />}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Forklift</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.brand} {deleteTarget?.model}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ImageManager({ imageUrl, images, uploading, setUploading, onImageUrlChange, onImagesChange }) {
  const [urlInput, setUrlInput] = useState("");
  const allImages = [imageUrl, ...images].filter(Boolean);
  const MAX = 8;

  const removeImage = (i) => {
    if (i === 0) {
      onImageUrlChange(images[0] || "");
      onImagesChange(images.slice(1));
    } else {
      const next = [...images];
      next.splice(i - 1, 1);
      onImagesChange(next);
    }
  };

  const addUrl = (url) => {
    if (!url.trim()) return;
    if (allImages.length >= MAX) { toast.error(`Max ${MAX} photos per machine`); return; }
    if (!imageUrl) { onImageUrlChange(url.trim()); }
    else { onImagesChange([...images, url.trim()]); }
  };

  const handleMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const available = MAX - allImages.length;
    if (available <= 0) { toast.error(`Max ${MAX} photos per machine`); return; }
    const toUpload = files.slice(0, available);
    setUploading(true);
    const newUrls = [];
    try {
      for (const file of toUpload) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: too large (max 10MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload-image`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
      }
      if (newUrls.length) {
        const combined = [imageUrl, ...images, ...newUrls].filter(Boolean);
        onImageUrlChange(combined[0]);
        onImagesChange(combined.slice(1));
        toast.success(`${newUrls.length} photo(s) uploaded!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const resolveUrl = (url) => url?.startsWith("/api/") ? `${API.replace('/api', '')}${url}` : url;

  return (
    <div className="space-y-3">
      {/* Thumbnail grid */}
      {allImages.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {allImages.map((url, i) => (
            <div key={i} className="relative group w-20 h-16 flex-shrink-0">
              <img
                src={resolveUrl(url)}
                alt=""
                className="w-full h-full object-cover rounded-sm border border-gray-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {i === 0 && (
                <span className="absolute top-0 left-0 text-[8px] font-bold bg-yellow-400 text-gray-900 px-1 py-0.5 leading-none rounded-sm">
                  COVER
                </span>
              )}
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full items-center justify-center hidden group-hover:flex hover:bg-red-600 shadow-sm"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload + URL input row */}
      {allImages.length < MAX && (
        <div className="flex gap-2 flex-wrap items-center">
          <label
            data-testid="form-image-upload"
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-sm cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading..." : "Upload photos"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              multiple
              disabled={uploading}
              onChange={handleMultiUpload}
            />
          </label>
          <span className="text-xs text-gray-400">or</span>
          <div className="flex gap-1.5 flex-1 min-w-[200px]">
            <Input
              data-testid="form-image-url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { addUrl(urlInput); setUrlInput(""); } }}
              placeholder="Paste image URL..."
              className="flex-1 text-sm"
            />
            <button
              type="button"
              onClick={() => { addUrl(urlInput); setUrlInput(""); }}
              className="px-3 py-2 text-sm font-medium bg-gray-900 text-white hover:bg-gray-700 rounded-sm"
            >
              Add
            </button>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-400">JPEG, PNG, WebP or GIF. Max 10MB per file. First photo = cover.</p>
    </div>
  );
}

function FormFields({
  form, handleChange, uploading, setUploading,
  suppliers, getSupplierById,
  showAddSupplier, setShowAddSupplier,
  newSupplierName, setNewSupplierName,
  newSupplierColor, setNewSupplierColor,
  handleAddSupplier, addingSupplier,
}) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">

        {/* Brand — dropdown */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Brand *</label>
          <Select value={form.brand || ""} onValueChange={(v) => handleChange("brand", v)}>
            <SelectTrigger data-testid="form-brand">
              <SelectValue placeholder="Select brand..." />
            </SelectTrigger>
            <SelectContent>
              {form.brand && !BRANDS.includes(form.brand) && (
                <SelectItem value={form.brand}>{form.brand}</SelectItem>
              )}
              {BRANDS.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Model</label>
          <Input data-testid="form-model" value={form.model} onChange={(e) => handleChange("model", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Condition</label>
          <Select value={form.condition} onValueChange={(v) => handleChange("condition", v)}>
            <SelectTrigger data-testid="form-condition"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="used">Used</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Price ($)</label>
          <Input data-testid="form-price" type="number" value={form.price} onChange={(e) => handleChange("price", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Capacity (lbs)</label>
          <Input data-testid="form-capacity" type="number" value={form.capacity_lbs} onChange={(e) => handleChange("capacity_lbs", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Engine Type</label>
          <Select value={form.engine_type} onValueChange={(v) => handleChange("engine_type", v)}>
            <SelectTrigger data-testid="form-engine"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="propane">Propane</SelectItem>
              <SelectItem value="diesel">Diesel</SelectItem>
              <SelectItem value="electric">Electric</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Mast Stages</label>
          <Select value={form.mast_stages} onValueChange={(v) => handleChange("mast_stages", v)}>
            <SelectTrigger data-testid="form-mast"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 Stages</SelectItem>
              <SelectItem value="3">3 Stages</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Tire Type</label>
          <Select value={form.tire_type} onValueChange={(v) => handleChange("tire_type", v)}>
            <SelectTrigger data-testid="form-tires"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pneumatic">Pneumatic</SelectItem>
              <SelectItem value="cushion">Cushion</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Drive</label>
          <Select value={form.drive} onValueChange={(v) => handleChange("drive", v)}>
            <SelectTrigger data-testid="form-drive"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2wd">2WD</SelectItem>
              <SelectItem value="4wd">4WD</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Year</label>
          <Input data-testid="form-year" type="number" value={form.year} onChange={(e) => handleChange("year", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Hours Used</label>
          <Input data-testid="form-hours" type="number" value={form.hours_used} onChange={(e) => handleChange("hours_used", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Status</label>
          <Select value={form.status} onValueChange={(v) => handleChange("status", v)}>
            <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Admin-only section */}
        <div className="col-span-2 md:col-span-3 border-t border-dashed border-gray-200 pt-4 mt-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Internal Info (Admin Only)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Supplier — dynamic from backend */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Supplier / Fournisseur</label>
              <Select
                value={form.supplier || ""}
                onValueChange={(v) => {
                  if (v === "__add_new__") {
                    setShowAddSupplier(true);
                  } else {
                    handleChange("supplier", v);
                    setShowAddSupplier(false);
                  }
                }}
              >
                <SelectTrigger data-testid="form-supplier">
                  <SelectValue placeholder="Select supplier...">
                    {form.supplier && (() => {
                      const s = getSupplierById(form.supplier);
                      return s ? (
                        <span className="flex items-center gap-2">
                          <span style={{ backgroundColor: s.color }} className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" />
                          {s.name}
                        </span>
                      ) : "Select supplier...";
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 italic">No suppliers yet — add one below</div>
                  ) : (
                    suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex items-center gap-2">
                          <span style={{ backgroundColor: s.color }} className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" />
                          {s.name}
                        </span>
                      </SelectItem>
                    ))
                  )}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <SelectItem value="__add_new__">
                      <span className="flex items-center gap-2 text-blue-600 font-medium">
                        <Plus className="w-3.5 h-3.5" /> Add new supplier...
                      </span>
                    </SelectItem>
                  </div>
                </SelectContent>
              </Select>

              {/* Inline add-supplier panel */}
              {showAddSupplier && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-sm space-y-3">
                  <p className="text-xs font-semibold text-gray-600">New Supplier</p>
                  <Input
                    data-testid="new-supplier-name"
                    value={newSupplierName}
                    onChange={(e) => setNewSupplierName(e.target.value)}
                    placeholder="e.g. Reparalift, A1 Machinery..."
                    className="bg-white text-sm"
                    onKeyDown={(e) => e.key === "Enter" && handleAddSupplier()}
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Choose a color:</p>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PALETTE.map((c) => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`color-swatch-${c.replace('#', '')}`}
                          onClick={() => setNewSupplierColor(c)}
                          style={{ backgroundColor: c }}
                          className={`w-6 h-6 rounded-full transition-all ${newSupplierColor === c ? "ring-2 ring-offset-1 ring-gray-800 scale-110" : "hover:scale-105"}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        style={{ backgroundColor: newSupplierColor }}
                        className="w-4 h-4 rounded-full inline-block flex-shrink-0"
                      />
                      <span className="text-xs text-gray-500">{newSupplierName || "Preview"}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="add-supplier-btn"
                      onClick={handleAddSupplier}
                      disabled={addingSupplier}
                      className="flex-1 px-3 py-1.5 text-xs font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm disabled:opacity-50"
                    >
                      {addingSupplier ? "Adding..." : "Add Supplier"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddSupplier(false); setNewSupplierName(""); }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Admin Notes */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Admin Notes (internal)</label>
              <Textarea
                data-testid="form-admin-notes"
                value={form.admin_notes || ""}
                onChange={(e) => handleChange("admin_notes", e.target.value)}
                placeholder={"Internal notes (cost, condition details, supplier contact...)\nNot visible to customers."}
                className="min-h-[80px] text-sm bg-gray-50"
              />
            </div>
          </div>
        </div>

        {/* Multi-image manager */}
        <div className="col-span-2 md:col-span-3">
          <label className="text-sm font-semibold text-gray-700 mb-2 block">
            Photos <span className="text-xs font-normal text-gray-400 ml-1">({[form.image_url, ...(form.images || [])].filter(Boolean).length} / 8 max)</span>
          </label>
          <ImageManager
            imageUrl={form.image_url}
            images={form.images || []}
            uploading={uploading}
            setUploading={setUploading}
            onImageUrlChange={(v) => handleChange("image_url", v)}
            onImagesChange={(v) => handleChange("images", v)}
          />
        </div>

        <div className="col-span-2 md:col-span-3">
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Description (EN)</label>
          <Textarea data-testid="form-desc-en" value={form.description_en} onChange={(e) => handleChange("description_en", e.target.value)} />
        </div>
        <div className="col-span-2 md:col-span-3">
          <label className="text-sm font-semibold text-gray-700 mb-1 block">Description (FR)</label>
          <Textarea data-testid="form-desc-fr" value={form.description_fr} onChange={(e) => handleChange("description_fr", e.target.value)} />
        </div>
      </div>
    </>
  );
}
